import {
  AFPTable,
  Pagination,
  Button,
  AFPTableRowAction,
  Spinner,
} from '@gsa/afp-component-library';
import OverlaySpinner from '../../components/overlay-spinner';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppAbility } from '@gsa/afp-shared-ui-utils';
import useDidMountEffect from '../../hooks/use-did-mount';
import { useEquipmentDetailCodes } from './equipment-code-detail-provider';
import { SI_LABELS } from '../standard-items/constants';
import EquipmentCodeAssociationFilter from './equipment-code-association-filter-panel';
import { OPERATIONS, SUBJECTS } from '../../utilities/constants';
import { InputType } from '../../components/forms/eq-association-table-elements';

const actionList = [
  {
    icon: 'edit',
    label: 'Edit',
  },
  {
    icon: 'delete',
    label: 'Remove',
  },
];

const AssociatedStandardItemsTable = () => {
  const {
    selectedEquipmentCode: selectedItem,
    getMultipleOptions,
    associationList,
    getEquipmentAssociations,
    isSinEcAssociationLoading,
    MODAL_MODES,
    setData,
    selectedAssociations,
    selectAllSelected,
    clearCheckBox,
  } = useEquipmentDetailCodes();

  const ability = useAppAbility();

  const multipleOptions = [
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '8',
      },
      uniqueKey: 'program',
      includeValueInLabel: true,
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: { operator: 'EQ', key: 'standardCodeMetadataId', value: '14' },
      uniqueKey: 'units',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '7',
      },
      uniqueKey: 'quantityRequired',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '3',
      },
      uniqueKey: 'tags',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      limit: 1000,
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '6',
      },
      uniqueKey: 'category',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '2',
      },
      uniqueKey: 'associationType',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '4',
      },
      uniqueKey: 'inputType',
    },
    {
      model: 'StandardsCodeModel',
      label: 'title',
      value: 'code',
      filter: {
        operator: 'EQ',
        key: 'standardCodeMetadataId',
        value: '5',
      },
      uniqueKey: 'criteria',
    },
  ];

  const canEdit = selectedItem?.canEdit;

  const canPartialEdit = selectedItem?.canPartialEdit;
  const isEqInContractYear = selectedItem?.isEquipmentCodeContractYear;
  const getActions = (row) => {
    const isSINReadOnly = row?.standardItem?.isStandardItemInOpenSolicitation;
    if (isSINReadOnly) {
      return [];
    }
    // if canPartialEdit only return edit
    if (canPartialEdit || isEqInContractYear) {
      return [actionList[0]];
    } else if (canEdit) {
      return actionList;
    }
    return [];
  };

  const [filters, setFilters] = useState({});
  const [order, setOrder] = useState([
    ['standardItem', 'standardItemNumber', 'ASC'],
  ]);
  const [paginationState, setPaginationState] = useState({
    limit: 10,
    offset: 0,
    currentPage: 1,
  });

  const tableRef = React.createRef();

  useEffect(() => {
    if (!selectedItem?.id) return;
    getMultipleOptions({
      variables: {
        options: multipleOptions,
      },
    });

    setFilters({
      operator: 'AND',
      value: [
        {
          operator: 'EQ',
          value: selectedItem.id,
          key: 'equipmentCodeId',
        },
        {
          operator: 'EQ',
          value: selectedItem.year,
          key: '$standardItem.year$',
        },
      ],
    });
  }, [selectedItem?.id]);

  useEffect(() => {
    if (selectAllSelected) {
      setPaginationState((prev) => ({
        ...prev,
        limit: associationList?.count,
      }));
      const checkbox = document.getElementById('select-row-select-all-header');
      checkbox.checked = true;
    } else {
      setPaginationState((prev) => ({ ...prev, limit: 10 }));
    }
  }, [selectAllSelected]);

  useEffect(() => {
    if (selectAllSelected && associationList?.rows) {
      tableRef?.current?.toggleAllRowsSelected();
    }
  }, [associationList]);

  const getData = () => {
    const { limit, offset } = paginationState;
    getEquipmentAssociations({
      variables: { filters, limit, offset, order },
    });
  };

  useDidMountEffect(() => {
    getData();
  }, [filters, order]);

  useDidMountEffect(() => {
    getData();
    tableRef?.current?.toggleAllRowsSelected(false);
  }, [paginationState]);

  const handleSelectedAction = useCallback((e, row) => {
    setData('SET_EQUIPMENT_ASSOCIATION', row.original);
    setData('SET_FORM_MODAL', true);

    if (e === 'Edit') {
      setData('SET_MODAL_MODE', MODAL_MODES.EDIT_ASSOCIATION);
      setData('SET_SUCCESS_MESSAGE', '');
    }
    if (e === 'Remove') {
      setData('SET_MODAL_MODE', MODAL_MODES.REMOVE_ASSOCIATION);
      setData('SET_SUCCESS_MESSAGE', '');
    }
    return null;
  }, []);

  const columns = useMemo(() => {
    const columnList = [
      {
        Header: SI_LABELS.NUMBER,
        accessor: 'standardItem.standardItemNumber',
      },
      {
        Header: 'Type',
        accessor: 'associationType.title',
        Cell: ({ value }) => {
          return value?.length ? value : '-';
        },
      },
      {
        Header: 'Title',
        accessor: 'standardItem.title',
        Cell: ({ value }) => {
          return value?.length ? value : '-';
        },
      },
      {
        Header: 'Standard details',
        accessor: 'associationText',
        Cell: ({ value }) => {
          return value?.length ? value : '-';
        },
      },
      {
        Header: 'Minimums',
        accessor: 'inputType.title',
        Cell: InputType,
      },
    ];

    if (ability.can(OPERATIONS.Manage, SUBJECTS.EquipmentCodes)) {
      columnList.push({
        Header: 'Actions',
        sortable: false,
        headerClassName: 'cell-center',
        cellClassName: 'cell-center',
        Cell: (props) => {
          // eslint-disable-next-line react/prop-types
          const { row } = props;
          const actions = getActions(row.original);
          if (!actions?.length) return null;
          return (
            <AFPTableRowAction
              // eslint-disable-next-line react/prop-types
              actions={actions}
              onSelectAction={(evt) => handleSelectedAction(evt, row)}
              {...props}
            />
          );
        },
      });
    }

    return columnList;
  }, [
    selectedItem,
    canEdit,
    canPartialEdit,
    ability,
    getActions,
    handleSelectedAction,
  ]);

  const handleClear = () => {
    tableRef?.current?.toggleAllRowsSelected(false);
    setData('SET_SELECTALL_SELECTED', false);
    setPaginationState((prev) => ({ ...prev, limit: 10 }));
  };

  useEffect(() => {
    if (clearCheckBox) {
      tableRef?.current?.toggleAllRowsSelected(false);
      setData('SET_CLEAR_CHECKBOX', false);
    }
  }, [clearCheckBox]);

  const handlePaginationChange = (currentPage, itemsPerPage) => {
    const offsetvalue = (currentPage - 1) * itemsPerPage;
    setPaginationState({
      limit: itemsPerPage,
      offset: offsetvalue,
      currentPage,
    });
  };

  useDidMountEffect(() => {
    handlePaginationChange(1, paginationState.limit);
    setOrder([['standardItem', 'standardItemNumber', 'ASC']]);
    getData();
  }, [filters]);

  if (!selectedItem)
    return <Spinner data-testid="lazy-load-spinner" size="small" />;

  return (
    <div className="equipment-association">
      <div className="grid-row grid-gap">
        <div className="desktop:grid-col-2 tablet-lg:grid-col-3" />
        <div className="desktop:grid-col-10 tablet-lg:grid-col-9">
          <div className="grid-row margin-top-4 margin-bottom-6">
            <div className="grid-col padding-left-2">
              {selectedAssociations?.length ? (
                <span>
                  <b>{selectedAssociations.length}</b> Standard Items selected
                  <Button
                    onClick={handleClear}
                    className="usa-button usa-button--unstyled margin-left-1"
                    data-testid="standerd-item-clear-button"
                    label="Clear"
                    leftIcon={{
                      name: 'close',
                      className: 'usa-icon--size-2',
                    }}
                  />
                </span>
              ) : null}
            </div>
            <div className="gird-col">
              {selectedAssociations?.length &&
              ability.can(OPERATIONS.Manage, SUBJECTS.EquipmentCodes) ? (
                <>
                  <Button
                    variant="outline"
                    data-testid="standerd-item-copy-button"
                    onClick={() => {
                      setData('SET_FORM_MODAL', true);
                      setData('SET_MODAL_MODE', MODAL_MODES.COPY);
                    }}
                    label="Copy Standard Items to Eq. Codes"
                    leftIcon={{
                      name: 'content_copy',
                    }}
                  />
                  {!isEqInContractYear && !canPartialEdit && (
                    <Button
                      variant="secondary"
                      data-testid="standerd-item-bulk-delete-button"
                      onClick={() => {
                        setData('SET_FORM_MODAL', true);
                        setData(
                          'SET_MODAL_MODE',
                          MODAL_MODES.CONFIRM_BULK_DELETE,
                        );
                      }}
                      label="Remove Standard Items"
                      leftIcon={{
                        name: 'delete',
                      }}
                    />
                  )}
                </>
              ) : null}
              {(selectedItem?.canEdit || selectedItem?.canPartialEdit) &&
                ability.can(OPERATIONS.Manage, SUBJECTS.EquipmentCodes) && (
                  <Button
                    data-testid="standerd-item-add-button"
                    onClick={() => {
                      setData('SET_FORM_MODAL', true);
                      setData('SET_MODAL_MODE', MODAL_MODES.ADD_ASSOCIATION); // Reset
                      setData('SET_SUCCESS_MESSAGE', '');
                    }}
                    label="Add Standard Item"
                    leftIcon={{
                      name: 'add',
                    }}
                  />
                )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid-row grid-gap">
        <div className="desktop:grid-col-3 tablet-lg:grid-col-3 association-filter-wrapper margin-top-7">
          <EquipmentCodeAssociationFilter setFilters={setFilters} />
        </div>
        <div className="desktop:grid-col-9 tablet-lg:grid-col-9 association-table-wrapper margin-top-5">
          {isSinEcAssociationLoading && (
            <OverlaySpinner message="Loading Standard Items..." />
          )}
          <AFPTable
            testId="associations-table"
            ref={tableRef}
            columns={columns}
            data={associationList.rows}
            onSort={setOrder}
            defaultSort={order}
            onRowSelect={({ selectedFlatRows }) => {
              setData('SET_EQUIPMENT_ASSOCIATION_SELECTIONS', selectedFlatRows);
            }}
            selectable={ability.can(OPERATIONS.Manage, SUBJECTS.EquipmentCodes)}
            selectAllHeader="Select to copy"
            selectAllHeaderClicked={() => {
              const checkbox = document.getElementById(
                'select-row-select-all-header',
              );
              setData('SET_SELECTALL_SELECTED', checkbox?.checked);
            }}
          />

          {!selectAllSelected && associationList?.rows ? (
            <div className="padding-y-4">
              <Pagination
                itemsPerPageOptions={[10, 25, 50]}
                onPageChange={handlePaginationChange}
                variant="advanced"
                itemsPerPage={paginationState.limit}
                currentPage={paginationState.currentPage}
                itemsCount={associationList?.count}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AssociatedStandardItemsTable;
